import React, {ReactNode} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import {Divider} from '../Divider'
import {Text} from '../Text'
import {Toggle} from '../Toggle'

type BaseProps = {
  /** The title of the list item (required) */
  title: ReactNode
  /** Optional description text below the title */
  description?: ReactNode
  /** Optional prefix content (typically an icon) */
  prefix?: ReactNode
  /** Optional suffix content (typically an icon) */
  suffix?: ReactNode
  /** Optional content displayed at the end of the title row */
  endContent?: ReactNode
  /** Whether to show a divider at the bottom */
  hasDivider?: boolean
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Style prop for custom styling */
  style?: React.CSSProperties
  /** Whether to show a toggle instead of suffix icon */
  withToggle?: boolean
  /** Toggle change handler */
  onToggleChange?: (event: React.SyntheticEvent) => void
  /** Toggle checked state controlled from outside */
  toggleChecked?: boolean
  /** Toggle id */
  toggleId?: string
  /** Toggle disabled state controlled from outside */
  toggleDisabled?: boolean
}

type DivProps = BaseProps & {
  /** Render the list item as a div */
  as?: 'div'
}

type ButtonProps = BaseProps & {
  /** Render the list item as a button */
  as?: 'button'
  /** Click handler */
  onClick?: (event: React.MouseEvent) => void
}

type LinkProps = BaseProps & {
  /** Render the list item as a link */
  as?: 'link'
  /** Link href */
  href?: string
  /** Link target */
  target?: string
  /** Link rel */
  rel?: string
  /** Click handler */
  onClick?: (event: React.MouseEvent) => void
}

export type ListItemProps = DivProps | ButtonProps | LinkProps

const ListItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  cursor: pointer;
  transition: background-color 0.2s ease;
  ${linariaMq.desktopXs} {
    &:hover,
    &:active {
      background-color: ${cssTheme.colors.background.neutral.hover};
    }
  }

  &.--as-button,
  &.--as-link {
    border: none;
    background: none;
    text-align: left;
    font: inherit;
    color: inherit;
    padding: 0 ${cssTheme.layout.spacing.s500};
    text-decoration: none;
    -webkit-appearance: none;
    -webkit-tap-highlight-color: transparent;
    ${linariaMq.desktopXs} {
      &:hover,
      &:active {
        background-color: ${cssTheme.colors.background.neutral.hover};
      }
    }
  }
`

const Wrapper = styled.div`
  display: flex;
  padding: ${cssTheme.layout.spacing.s500} 0;
  gap: ${cssTheme.layout.spacing.s300};
  align-items: flex-start;
  cursor: inherit;
`

export const PrefixWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

export const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  min-width: 0;
`

const TopRow = styled.div`
  display: flex;
  gap: ${cssTheme.layout.spacing.s300};
  justify-content: space-between;
  width: 100%;
`

export const EndContent = styled.div`
  ${cssTheme.typography.bodyS};
  color: ${cssTheme.colors.content.neutral.primary};
  flex-shrink: 0;
  white-space: pre;
`

export const Description = styled.div`
  ${cssTheme.typography.bodyS};
  color: ${cssTheme.colors.content.neutral.secondary};
  white-space: pre;
`

export const SuffixWrapper = styled.div`
  display: flex;
  gap: 4px;
  height: 22px;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
`

export const ListItem = ({
  title,
  description,
  prefix,
  suffix,
  withToggle,
  onToggleChange,
  toggleChecked,
  toggleDisabled,
  toggleId = '',
  endContent,
  hasDivider = false,
  className,
  dataId,
  style,
  as = 'button',
  ...restProps
}: ListItemProps) => {
  const elementType = as === 'link' ? 'a' : as === 'button' ? 'button' : 'div'

  const wrapperElement = withToggle ? 'label' : 'div'
  const wrapperProps = withToggle && toggleId ? {htmlFor: toggleId} : {}

  const renderSuffix = () => {
    if (withToggle && onToggleChange) {
      return (
        <SuffixWrapper>
          <Toggle
            id={toggleId}
            checked={toggleChecked || false}
            onChange={onToggleChange}
            standalone={false}
            disabled={toggleDisabled}
          />
        </SuffixWrapper>
      )
    }
    if (suffix) {
      return <SuffixWrapper>{suffix}</SuffixWrapper>
    }
    return null
  }

  return (
    <div>
      <ListItemContainer
        as={elementType}
        className={cx(className, as && `--as-${as}`)}
        data-id={dataId}
        style={style}
        {...restProps}
      >
        <Wrapper as={wrapperElement} {...wrapperProps}>
          {prefix && <PrefixWrapper>{prefix}</PrefixWrapper>}
          <ContentColumn>
            <TopRow>
              {/* `tag="div"` because callers pass arbitrary nodes: a heading or
                  paragraph here would nest illegally around them. */}
              <Text variant="titleS" tag="div">
                {title}
              </Text>
              {endContent && (
                <Text variant="bodyS" tag="div">
                  {endContent}
                </Text>
              )}
            </TopRow>
            {description && (
              <Text
                variant="bodyS"
                tag="div"
                colorPath="content.neutral.secondary"
              >
                {description}
              </Text>
            )}
          </ContentColumn>
          {renderSuffix()}
        </Wrapper>
        {hasDivider && <Divider />}
      </ListItemContainer>
    </div>
  )
}
